import { captureException } from "@sentry/react";
import { useEffect } from "react";

import { AnalyticsEvent, useUnauthUrlFields } from "@shared/hooks";
import type { AnalyticsEventProps } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";

interface OpenCatalogAnalyticsEventProps extends Pick<AnalyticsEventProps, "payload"> {
  event_name: AnalyticsEvent;
}

const logAnalyticsEvent = (input: OpenCatalogAnalyticsEventProps) => {
  const eventUrl = "/log_analytics_events";
  fetch(eventUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ event: input }),
  }).catch(error => {
    captureException(error, { level: "warning" });
  });
};

/**
 * This hook is only meant for logging page views on the unauthenticated catalog.
 *
 * It uses fetch to POST to a REST endpoint for events that behaves slightly differently from our graphql analytics
 * mutation. Unlike our graphql analytics mutation, this REST endpoint sets the analytics cookie if it's not set.
 * Our analytics mutation does not set the analytics cookie, because this will cause a race condition between all
 * analytics events logged on loading the page, if the cookie is not already set.
 * Hence it relies on the rails server having set that cookie on the initial request to render the app. However since
 * our SSR pages are rendered on the express server and not through the rails server it does not set our analytics
 * cookie causing the graphql analytics mutation to fail.
 *
 * In the future if needed this hook can be moved to a shared location and used for other SSR pages.
 *
 */
const useOpenCatalogPageViewLogger = () => {
  const { pathname } = useLocation();
  const unauthUrlFields = useUnauthUrlFields();

  return useEffect(() => {
    logAnalyticsEvent({
      event_name: AnalyticsEvent.UNAUTH_PAGE_VIEW,
      payload: {
        ...unauthUrlFields,
        url: pathname,
      },
    });
  }, [pathname]);
};

export { useOpenCatalogPageViewLogger };
